import React, { useState } from 'react';
import { UserProfile, getTierForExp, TIERS, saveUserProfile } from '../../core/profile';
import { X, Copy, Check, Trophy, Award, Shield, Sparkles, Download, Upload } from 'lucide-react';
import confetti from 'canvas-confetti';

interface PlayerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: UserProfile;
  onUpdateProfile: (p: UserProfile) => void;
}

export const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  onUpdateProfile,
}) => {
  const [copiedSyncCode, setCopiedSyncCode] = useState(false);
  const [importJsonText, setImportJsonText] = useState('');
  const [showImportBox, setShowImportBox] = useState(false);

  if (!isOpen) return null;

  const currentTier = getTierForExp(profile.exp);
  const nextTier = TIERS.find((t) => t.minExp > profile.exp) || currentTier;
  const expProgress =
    nextTier.minExp === currentTier.minExp
      ? 100
      : Math.min(100, Math.round(((profile.exp - currentTier.minExp) / (nextTier.minExp - currentTier.minExp)) * 100));

  const handleCopySyncCode = () => {
    navigator.clipboard.writeText(profile.syncCode);
    setCopiedSyncCode(true);
    setTimeout(() => setCopiedSyncCode(false), 2000);
  };

  const handleExportJson = () => {
    const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(profile, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `joy_tennis_profile_${profile.syncCode}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  const handleImportJson = () => {
    try {
      const parsed = JSON.parse(importJsonText);
      if (parsed && parsed.syncCode && parsed.ntrpRating) {
        onUpdateProfile(parsed);
        saveUserProfile(parsed);
        setShowImportBox(false);
        setImportJsonText('');
        confetti({ particleCount: 50, spread: 60 });
      } else {
        alert('无效的档案 JSON 格式');
      }
    } catch (e) {
      alert('解析失败，请检查 JSON 格式');
    }
  };

  const achievements = [
    { id: 'first_serve_ace', title: 'ACE 发球之王', desc: '单场发出 3 记以上内角 ACE 球', unlocked: true, icon: '⚡' },
    { id: 'crosscourt_master', title: '斜线相持宗师', desc: '在战术沙盘中执行 50 拍百分比相持', unlocked: true, icon: '🎯' },
    { id: 'joy_club_member', title: '欢乐认证球友', desc: '正式加入 Joy Tennis Club 训练矩阵', unlocked: true, icon: '🎾' },
    { id: 'tiebreak_clutch', title: '大心脏抢七赢家', desc: '在抢七决胜局中绝杀翻盘', unlocked: false, icon: '👑' },
    { id: 'poach_king', title: '双打网前神偷', desc: '双打比赛成功抢网封网截击 5 次', unlocked: false, icon: '🦅' },
    { id: 'ntrp_elevated', title: 'NTRP 突破大师', desc: '实战战力评级达到 4.0 以上', unlocked: false, icon: '🏆' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/80 backdrop-blur-sm animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
          <div className="flex items-center space-x-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <h2 className="text-base font-bold text-white">个人战绩与俱乐部段位</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* 1. Player Card */}
          <div className="relative p-4 rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-slate-850 border border-slate-700/80 flex flex-col sm:flex-row items-center sm:items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 border-2 border-lime-400/80 flex items-center justify-center text-3xl shadow-glow-ball">
              {profile.avatar}
            </div>

            <div className="flex-1 text-center sm:text-left">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 mb-1">
                <span className="text-lg font-black text-white">{profile.name}</span>
                <span className="inline-block bg-lime-500/20 text-lime-400 border border-lime-500/30 text-xs px-2 py-0.5 rounded-full font-bold">
                  NTRP {profile.ntrpRating.toFixed(1)}
                </span>
              </div>

              <div className="flex items-center justify-center sm:justify-start gap-1.5 text-xs text-amber-400 font-bold mb-2">
                <span>{currentTier.icon}</span>
                <span>Lv.{profile.level} {currentTier.name}</span>
                <span className="text-slate-500">·</span>
                <span className="text-slate-400 text-[11px]">{currentTier.ntrpEquivalent}</span>
              </div>

              {/* EXP Bar */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] text-slate-400 font-semibold">
                  <span>经验值 EXP: {profile.exp}</span>
                  <span>目标: {nextTier.minExp} EXP ({expProgress}%)</span>
                </div>
                <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-lime-500 to-amber-400 transition-all duration-500"
                    style={{ width: `${expProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Match Statistics & Sync Code */}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
              <div className="text-[10px] text-slate-400">总胜率</div>
              <div className="text-base font-black text-lime-400 mt-0.5">
                {profile.matchesPlayed > 0 ? Math.round((profile.matchesWon / profile.matchesPlayed) * 100) : 0}%
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {profile.matchesWon}胜 {profile.matchesPlayed - profile.matchesWon}负
              </div>
            </div>

            <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
              <div className="text-[10px] text-slate-400">已完成特训</div>
              <div className="text-base font-black text-sky-400 mt-0.5">{profile.drillsCompleted} 组</div>
              <div className="text-[10px] text-slate-500 mt-0.5">动作纠错 & 沙盘</div>
            </div>

            <div className="p-3 bg-slate-950 rounded-xl border border-slate-800">
              <div className="text-[10px] text-slate-400">免密同步码</div>
              <div className="text-xs font-mono font-bold text-amber-400 mt-1 flex items-center justify-center gap-1">
                <span>{profile.syncCode}</span>
                <button onClick={handleCopySyncCode} className="hover:text-white" title="复制">
                  {copiedSyncCode ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                </button>
              </div>
              <div className="text-[9px] text-slate-500 mt-0.5">跨设备同步</div>
            </div>
          </div>

          {/* 3. Club Achievements */}
          <div>
            <h3 className="text-xs font-bold text-slate-400 mb-2.5 flex items-center gap-1.5 uppercase tracking-wider">
              <Award className="w-4 h-4 text-amber-400" />
              <span>俱乐部成就勋章 (Achievements)</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {achievements.map((ach) => (
                <div
                  key={ach.id}
                  className={`p-2.5 rounded-xl border flex items-center space-x-2.5 ${
                    ach.unlocked
                      ? 'bg-slate-950 border-amber-500/30'
                      : 'bg-slate-950/40 border-slate-850 opacity-50'
                  }`}
                >
                  <span className="text-xl">{ach.icon}</span>
                  <div className="flex-1 leading-tight">
                    <div className="text-xs font-bold text-white flex items-center justify-between">
                      <span>{ach.title}</span>
                      {ach.unlocked && <span className="text-[9px] text-amber-400 font-semibold">已解锁</span>}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">{ach.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 4. Import / Export Data */}
          <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs">
            <div className="flex items-center space-x-2">
              <button
                onClick={handleExportJson}
                className="flex items-center space-x-1 text-slate-400 hover:text-white px-2.5 py-1 bg-slate-800/80 rounded-lg"
              >
                <Download className="w-3.5 h-3.5" />
                <span>导出战绩 JSON</span>
              </button>
              <button
                onClick={() => setShowImportBox(!showImportBox)}
                className="flex items-center space-x-1 text-slate-400 hover:text-white px-2.5 py-1 bg-slate-800/80 rounded-lg"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>导入战绩</span>
              </button>
            </div>
          </div>

          {showImportBox && (
            <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 space-y-2">
              <textarea
                value={importJsonText}
                onChange={(e) => setImportJsonText(e.target.value)}
                placeholder="粘贴之前导出的档案 JSON 数据..."
                className="w-full h-20 bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-xs font-mono"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowImportBox(false)}
                  className="px-3 py-1 bg-slate-800 text-slate-300 rounded-lg text-xs"
                >
                  取消
                </button>
                <button
                  onClick={handleImportJson}
                  className="px-3 py-1 bg-lime-500 text-slate-950 font-bold rounded-lg text-xs"
                >
                  确认导入
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
